import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';

import { LanguageType, defaultLanguage } from '../config/languages';
import { TRANSLATIONS_SEED } from '../config/translations.seed';
import { translationApi, TranslationRow } from '../helper/translations';
import { TFunction, TranslationMap } from '../types/i18n.types';
import { AuthService } from '../service/auth.service';

interface LanguageContextType {
  language: LanguageType;
  setLanguage: (lang: LanguageType) => void;

  t: TFunction;
  translateLabel: TFunction;
  translateByModule: (key: string, moduleName: string) => string;

  translations: TranslationMap;
  loading: boolean;
  error: Error | null;
  reloadTranslations: () => Promise<void>;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

function seedToMap(): TranslationMap {
  return TRANSLATIONS_SEED.reduce<TranslationMap>((acc, item) => {
    const mapKey = item.module ? `${item.key}__module_${item.module}` : item.key;
    
    acc[mapKey] = {
      en: item.values.en,
      ru: item.values.ru,
      uk: item.values.uk,
    };
    return acc;
  }, {});
}

function dbToMap(rows: TranslationRow[]): TranslationMap {
  return rows.reduce<TranslationMap>((acc, row) => {
    const uniqueKey = row.module 
      ? `${row.key}__module_${row.module}` 
      : row.key;
    
    acc[uniqueKey] = {
      en: row.english,
      ru: row.russian,
      uk: row.ukrainian,
    };
    return acc;
  }, {});
}

function mergeTranslations(
  dbMap: TranslationMap,
  seedMap: TranslationMap
): TranslationMap {
  const result: TranslationMap = { ...seedMap };

  Object.keys(dbMap).forEach((key) => {
    result[key] = {
      ...result[key],
      ...dbMap[key],
    };
  });

  return result;
}

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<LanguageType>(() => {
    return (
      (localStorage.getItem('app-language') as LanguageType) ||
      defaultLanguage
    );
  });

  const [translations, setTranslations] = useState<TranslationMap>(seedToMap());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const loadTranslations = async () => {
    const token = AuthService.getToken();
    
    if (!token) {
      console.log('[i18n] No auth token, using seed translations only');
      setTranslations(seedToMap());
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const rows = await translationApi.getAll();
      const seedMap = seedToMap();
      
      if (rows && rows.length > 0) {
        const dbMap = dbToMap(rows);
        const merged = mergeTranslations(dbMap, seedMap);
        
        setTranslations(merged);
        
      } else {
        setTranslations(seedMap);
      }
    } catch (err: any) {
      setError(err);
      
      if (err.response?.status === 401 || err.response?.status === 403) {
      } else {
        console.error('[i18n] Failed to load translations from DB:', err);
      }
      
      setTranslations(seedToMap());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTranslations();
    
    const handleTokenChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const token = customEvent.detail?.token;
            
      if (token) {
        loadTranslations();
      } else {
        setTranslations(seedToMap());
      }
    };
    
    window.addEventListener('auth-token-changed', handleTokenChange);
    
    return () => {
      window.removeEventListener('auth-token-changed', handleTokenChange);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('app-language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t: TFunction = (key, replacements) => {
    let text =
      translations[key]?.[language] ||
      translations[key]?.en ||
      key;

    if (replacements) {
      Object.entries(replacements).forEach(([k, v]) => {
        text = text.replace(new RegExp(`{${k}}`, 'g'), String(v));
      });
    }

    return text;
  };

  const translateLabel: TFunction = (key) => {
    return (
      translations[key]?.[language] ??
      translations[key]?.en ??
      key
    );
  };

  const translateByModule = (key: string, moduleName: string): string => {
    const moduleKey = `${key}__module_${moduleName}`;

    let moduleTranslation = 
      translations[moduleKey]?.[language] ??
      translations[moduleKey]?.en;
    
    if (moduleTranslation) {
      return moduleTranslation;
    }
    
    const pluralKey = `${key}s__module_${moduleName}`;
    moduleTranslation = 
      translations[pluralKey]?.[language] ??
      translations[pluralKey]?.en;
        
    if (moduleTranslation) {
      return moduleTranslation;
    }
    
    return translateLabel(key);
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        t,
        translateLabel,
        translateByModule,
        translations,
        loading,
        error,
        reloadTranslations: loadTranslations,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used inside LanguageProvider');
  }
  return ctx;
};